// VOID Conflict — StartScreen Orchestrator
//
// Manages the pre-game Fullscreen Startseite (Main Menu):
// 1. Fullscreen background (assets/background.jpeg, crop to fill, with dark scrim)
// 2. Commander Avatar card with Level-Badge in in-game plasma-cost styling (.card-cost)
// 3. Dynamic call-sign and MMR display
// 4. Action buttons: START GAME, LADDER, LOGIN/ACCOUNT (Red/Blue status glow), OPTIONS
// 5. Standby management: Keeps battle simulation idle until START GAME is activated

import { getAuth, addIdentityListener, ensureIdentity, getDisplayName, getMMR } from '../net/playerIdentity.js';
import { progress } from '../engine/progress.js';
import { inventory } from '../engine/inventory.js';
import { resolveAvatarImageUrl } from './avatarImage.js';
import { getCardImageUrl, CARDS_DATA } from '../data/cards.js';
import { getCardThumbUrl } from '../data/cardThumbUrl.js';
import { REEL_3_STYLES_MAP } from '../engine/slotMachine.js';
import { checkAlphaAccess } from './alphaGate.js';
import { showLeaderboardModal } from './leaderboardModal.js';
import { showAccountFullscreenModal } from './accountFullscreenModal.js';
import { showOptionsFullscreenModal } from './optionsFullscreenModal.js';
import { showRecoveryCode } from './accountOverlay.js';
import { inboxManager } from '../net/inboxManager.js';
import { showDataFeedModal } from './dataFeedModal.js';
import { showLegalModal } from './legalModal.js';

export function isPwaMode() {
  if (typeof window === 'undefined') return false;
  // 1. html.is-pwa set by early detection script in index.html
  if (typeof document !== 'undefined' && document.documentElement?.classList?.contains('is-pwa')) {
    return true;
  }
  // 2. iOS Safari standalone (Home Screen web clip)
  if (window.navigator && window.navigator.standalone === true) {
    return true;
  }
  // 3. Modern display-mode media queries
  if (typeof window.matchMedia === 'function') {
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.matchMedia('(display-mode: window-controls-overlay)').matches
    ) {
      return true;
    }
  }
  // 4. Android TWA / app referrer
  if (typeof document !== 'undefined' && document.referrer && document.referrer.startsWith('android-app://')) {
    return true;
  }
  // 5. Explicit URL parameter (e.g. from PWA shortcut or start_url)
  if (typeof window.location !== 'undefined' && window.location?.search) {
    if (/[?&](mode|source|pwa)=pwa\b/i.test(window.location.search)) {
      return true;
    }
  }
  return false;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    window.__deferredInstallPrompt = e;
  });
  window.addEventListener('appinstalled', () => {
    window.__deferredInstallPrompt = null;
    if (typeof document !== 'undefined') {
      document.documentElement?.classList?.add('is-pwa');
      document.querySelectorAll('.start-app-install-btn').forEach((btn) => {
        btn.style.display = 'none';
      });
    }
  });
  if (typeof window.matchMedia === 'function') {
    try {
      const pwaMql = window.matchMedia('(display-mode: standalone)');
      pwaMql.addEventListener?.('change', (e) => {
        if (e.matches && typeof document !== 'undefined') {
          document.documentElement?.classList?.add('is-pwa');
          document.querySelectorAll('.start-app-install-btn').forEach((btn) => {
            btn.style.display = 'none';
          });
        }
      });
    } catch (_) {}
  }
}

export class StartScreen {
  constructor(game) {
    this.game = game;
    this.container = null;
    this.isVisible = true;

    this.init();
    addIdentityListener(() => this.updateProfileDisplay());
    progress.addListener(() => this.updateProfileDisplay());
    inventory.addListener(() => this.updateProfileDisplay());
  }

  init() {
    let el = document.getElementById('start-screen');
    if (!el) {
      el = document.createElement('div');
      el.id = 'start-screen';
      el.className = 'start-screen-container active';
      document.body.appendChild(el);
    }
    this.container = el;
    // If static SSR-light markup already exists in index.html, hydrate instead of wiping DOM
    const hasStaticMarkup = el.querySelector('#btn-start-enter');
    if (!hasStaticMarkup) {
      this.render();
    }
    this.bindEvents();
    if (isPwaMode()) {
      const btnInstall = this.container.querySelector('#btn-start-install-app');
      if (btnInstall) btnInstall.style.display = 'none';
    }
    this.updateProfileDisplay();
    this.updateOnlineStatus();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.updateOnlineStatus());
      window.addEventListener('offline', () => this.updateOnlineStatus());
    }

    // Check inbox on boot (only when network uplink is active)
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      inboxManager.checkInbox();
    }

    // If arriving with ?alpha= or ?access=, process immediately so Call-Sign renders right away
    if (typeof window !== 'undefined' && window.location) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('alpha') || params.get('access')) {
        checkAlphaAccess({ prompt: true }).then(() => {
          this.updateProfileDisplay();
        });
      }
      const giftParam = params.get('gift') || params.get('voucher') || params.get('code');
      if (giftParam) {
        ensureIdentity().then(() => {
          inboxManager.redeemVoucher(giftParam).then((res) => {
            if (res.ok) {
              this.game?.audio?.playDailyJackpotFanfare?.();
              showDataFeedModal(this.game);
            }
          });
        });
      }
    }
  }

  render() {
    this.container.innerHTML = `
      <div class="start-screen-bg-layer">
        <div class="start-screen-bg-image"></div>
        <div class="start-screen-scrim"></div>
        <div class="start-screen-vignette"></div>
      </div>

      <div class="start-screen-content">
        <!-- Top Tech Header -->
        <header class="start-screen-header">
          <div class="start-header-status-pill">
            <span class="status-indicator-dot"></span>
            <span class="status-indicator-text">SYSTEM READY</span>
          </div>
          <button type="button" id="btn-start-install-app" class="start-app-install-btn" title="Install VOID Conflict as App" aria-label="Install App"${isPwaMode() ? ' style="display: none;"' : ''}>
            <img src="assets/ui/icons/icon-192.png" alt="" class="start-app-install-icon" width="46" height="46" />
            <span class="start-app-install-badge" aria-hidden="true">
              <svg class="start-app-install-svg" viewBox="0 0 20 20" width="13" height="13" fill="none">
                <path d="M10 3.25v8.5M6.25 8.5L10 12.25l3.75-3.75" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4 12.75v1.75a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1.75" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </header>

        <!-- Center Group: Hero Profile + Action Navigation with conscious spacing -->
        <div class="start-center-group">
          <!-- Center Hero: Commander Profile -->
          <div class="start-hero-section">
            <div class="start-avatar-card-wrap">
              <div class="start-avatar-card" id="start-avatar-card" role="button" tabindex="0" title="Arsenal · Commander Profile">
                <div class="start-avatar-inner">
                  <img id="start-avatar-img" class="start-avatar-img" src="${getCardThumbUrl('1_Hypermarine.png', 'm')}" alt="Commander Avatar" />
                  <div class="start-avatar-scanline"></div>
                </div>
                <!-- Level badge styled exactly like in-game card plasma-cost -->
                <div id="start-level-badge" class="start-level-badge card-cost" title="Player Level">1</div>
              </div>
              <div class="start-commander-meta">
                <div id="start-commander-name" class="start-commander-name" role="button" tabindex="0" title="Commander Dossier & Account">GUEST COMMANDER</div>
                <div id="start-commander-mmr" class="start-commander-mmr" role="button" tabindex="0" title="Ladder & Leaderboard">-- MMR</div>
              </div>
            </div>
          </div>

          <!-- Action Navigation Buttons -->
          <nav class="start-menu-actions" aria-label="Main Navigation">
            <button type="button" id="btn-start-enter" class="start-btn start-btn-primary">
              <span class="start-btn-text">ENTER THE VOID</span>
            </button>

            <button type="button" id="btn-start-ladder" class="start-btn start-btn-gold">
              <span class="start-btn-text">LADDER</span>
            </button>

            <button type="button" id="btn-start-login" class="start-btn start-btn-login status-guest">
              <span class="start-btn-text" id="start-login-text">COMMANDER</span>
            </button>

            <button type="button" id="btn-start-options" class="start-btn start-btn-secondary">
              <span class="start-btn-text">OPTIONS</span>
            </button>
          </nav>
        </div>

        <!-- Footer Meta -->
        <footer class="start-screen-footer">
          <button type="button" id="start-btn-imprint" class="start-footer-link" aria-label="Legal Notice & Privacy Policy">Imprint</button>
          <span class="start-footer-sep">//</span>
          <h1 class="start-footer-tag">VOID CONFLICT</h1>
          <span class="start-footer-sep">//</span>
          <a id="start-build-tag" class="start-footer-build-tag start-footer-link" href="https://discord.gg/UECTSvDB4u" target="_blank" rel="noopener noreferrer" title="Join the VOID Conflict Discord">96323F</a>
        </footer>
      </div>
    `;
  }

  bindEvents() {
    const avatarCard = this.container.querySelector('#start-avatar-card');
    avatarCard?.addEventListener('click', async (e) => {
      e.stopPropagation();
      this.game?.audio?.playUiClick?.();
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        if (this.game?.showroomModal) {
          this.hide();
          this.game.showroomModal.open({ tab: 'misc', miscCategory: 'profile' });
        }
        return;
      }
      const granted = await checkAlphaAccess({ prompt: true });
      if (!granted) return;

      if (this.game?.showroomModal) {
        this.hide();
        this.game.showroomModal.open({ tab: 'misc', miscCategory: 'profile' });
      }
    });
    avatarCard?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        avatarCard.click();
      }
    });

    const commanderName = this.container.querySelector('#start-commander-name');
    commanderName?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.game?.audio?.playUiClick?.();
      showAccountFullscreenModal(this.game);
    });
    commanderName?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        commanderName.click();
      }
    });

    const commanderMmr = this.container.querySelector('#start-commander-mmr');
    commanderMmr?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.game?.audio?.playUiClick?.();
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return;
      }
      showLeaderboardModal(this.game);
    });
    commanderMmr?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        commanderMmr.click();
      }
    });

    const buildTag = this.container.querySelector('#start-build-tag');
    buildTag?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
    });

    const btnEnter = this.container.querySelector('#btn-start-enter');
    btnEnter?.addEventListener('click', async () => {
      this.game?.audio?.playUiClick?.();
      await this.handleEnterTheVoid();
    });

    const btnLadder = this.container.querySelector('#btn-start-ladder');
    btnLadder?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return;
      }
      showLeaderboardModal(this.game);
    });

    const btnLogin = this.container.querySelector('#btn-start-login');
    btnLogin?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      showAccountFullscreenModal(this.game);
    });

    const btnOptions = this.container.querySelector('#btn-start-options');
    btnOptions?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      showOptionsFullscreenModal(this.game);
    });

    const btnImprint = this.container.querySelector('#start-btn-imprint');
    btnImprint?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.game?.audio?.playUiClick?.();
      showLegalModal('imprint', this.game);
    });

    const btnInstall = this.container.querySelector('#btn-start-install-app');
    btnInstall?.addEventListener('click', async () => {
      this.game?.audio?.playUiClick?.();
      const prompt = window.__deferredInstallPrompt;
      if (prompt) {
        try {
          prompt.prompt();
          const { outcome } = await prompt.userChoice;
          if (outcome === 'accepted') {
            window.__deferredInstallPrompt = null;
          }
        } catch (err) {
          console.warn('[PWA] Prompt error:', err);
        }
      } else {
        this.showInstallGuidanceModal();
      }
    });
  }

  showInstallGuidanceModal() {
    const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    const layer = document.createElement('div');
    layer.className = 'fullscreen-dialog-overlay active';
    layer.style.zIndex = '12060';

    layer.innerHTML = `
      <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
        <div class="fullscreen-dialog-bg-image"></div>
        <div class="fullscreen-dialog-scrim"></div>
        <div class="fullscreen-dialog-vignette"></div>
      </div>
      <div class="fullscreen-dialog-container" style="justify-content: center; align-items: center; min-height: 100vh;">
        <div class="fullscreen-card-panel" style="max-width: 460px; width: 92%; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 16px; padding: clamp(24px, 4vh, 36px) clamp(20px, 3.5vw, 32px); border: 1.5px solid rgba(0, 240, 255, 0.45); box-shadow: 0 0 24px rgba(0, 240, 255, 0.25);">
          <div class="fullscreen-dialog-status-pill status-pill-circular" style="margin-bottom: -2px;">
            <span class="status-indicator-dot"></span>
          </div>
          <div style="font-family: 'Orbitron', sans-serif; font-size: clamp(18px, 2.2vw, 22px); font-weight: 900; letter-spacing: 2px; color: #00f0ff; text-shadow: 0 0 16px rgba(0, 240, 255, 0.45); margin: 0;">
            INSTALL APP
          </div>
          <div style="font-family: 'Rajdhani', sans-serif; font-size: clamp(15px, 1.8vw, 17px); font-weight: 600; line-height: 1.5; color: #e2e8f0; text-align: left; width: 100%;">
            ${isIOS ? `
              <div style="display: flex; flex-direction: column; gap: 10px;">
                <div><strong>1.</strong> Tap the <strong>Share</strong> button at the bottom of Safari.</div>
                <div><strong>2.</strong> Select <strong>"Add to Home Screen"</strong>.</div>
                <div><strong>3.</strong> Launch VOID Conflict directly from your home screen as a native full-screen app.</div>
              </div>
            ` : `
              <div style="display: flex; flex-direction: column; gap: 10px;">
                <div><strong>1.</strong> Click <strong>"Install App"</strong> in your browser menu.</div>
                <div><strong>2.</strong> Confirm the installation for instant 1-click full-screen access.</div>
              </div>
            `}
          </div>
          <button type="button" class="start-btn start-btn-primary" id="btn-install-close" style="width: 100%; height: 46px; margin-top: 6px;">
            <span class="start-btn-text">GOT IT</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(layer);

    const close = () => {
      this.game?.audio?.playUiClick?.();
      layer.remove();
    };

    layer.querySelector('#btn-install-close')?.addEventListener('click', close);
    layer.addEventListener('click', (e) => {
      if (e.target === layer) close();
    });
  }

  async handleEnterTheVoid() {
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

    if (!isOffline) {
      // 1. Check Alpha clearance (prompts modal if not yet granted)
      const granted = await checkAlphaAccess({ prompt: true });
      if (!granted) return;

      // 2. Ensure device has an identity registered if not yet established.
      // ensureIdentity() only ever returns a recoveryCode the one time it
      // genuinely registers a brand-new account (never on a returning
      // device) - show it now, since the server never sends it again after
      // this (see js/ui/accountOverlay.js).
      const auth = getAuth();
      if (!auth || !auth.accountId) {
        try {
          const identity = await ensureIdentity();
          if (identity && identity.recoveryCode) {
            showRecoveryCode(identity);
          }
        } catch (err) {
          console.warn('[StartScreen] ensureIdentity failed during enter:', err);
        }
      }

      // 3. Check for high-priority transmissions (forces dialog on login if unread/unclaimed)
      try {
        const inboxStatus = await inboxManager.checkInbox();
        if (inboxStatus?.hasHighPriority) {
          showDataFeedModal(this.game);
        }
      } catch {}
    }

    // 4. Hide start screen and enter archive/hub
    this.hide();
    if (this.game) {
      this.game.deckBuilder?.openModal();
    }
  }

  updateOnlineStatus() {
    if (!this.container) return;
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

    // 1. Top Tech Header Status Pill
    const statusPill = this.container.querySelector('.start-header-status-pill');
    const statusDot = this.container.querySelector('.status-indicator-dot');
    const statusText = this.container.querySelector('.status-indicator-text');
    if (statusPill && statusText && statusDot) {
      if (isOffline) {
        statusPill.classList.add('offline');
        statusDot.classList.add('offline');
        statusText.textContent = 'OFFLINE // FLIGHT SIMULATION';
      } else {
        statusPill.classList.remove('offline');
        statusDot.classList.remove('offline');
        statusText.textContent = 'SYSTEM READY';
      }
    }

    // 2. Primary Action Button
    const btnEnter = this.container.querySelector('#btn-start-enter');
    const btnEnterText = btnEnter?.querySelector('.start-btn-text');
    if (btnEnterText) {
      btnEnterText.textContent = isOffline ? 'PLAY OFFLINE (SKIRMISH)' : 'ENTER THE VOID';
    }

    // 3. Ranked Ladder Button (disabled in offline mode)
    const btnLadder = this.container.querySelector('#btn-start-ladder');
    if (btnLadder) {
      if (isOffline) {
        btnLadder.classList.add('start-btn-disabled-offline');
        btnLadder.setAttribute('title', 'Network uplink required for Ranked Ladder');
      } else {
        btnLadder.classList.remove('start-btn-disabled-offline');
        btnLadder.removeAttribute('title');
      }
    }
  }

  updateProfileDisplay() {
    if (!this.container) return;
    const auth = getAuth();
    const hasAccount = !!(auth && auth.accountId);

    const nameEl = this.container.querySelector('#start-commander-name');
    const mmrEl = this.container.querySelector('#start-commander-mmr');
    const badgeEl = this.container.querySelector('#start-level-badge');
    const imgEl = this.container.querySelector('#start-avatar-img');
    const cardEl = this.container.querySelector('.start-avatar-card');
    const btnLogin = this.container.querySelector('#btn-start-login');
    const loginIcon = this.container.querySelector('#start-login-icon');
    const loginText = this.container.querySelector('#start-login-text');

    if (hasAccount) {
      const displayName = getDisplayName() || 'Commander';
      const mmr = getMMR();
      const levelProgress = progress.getLevelProgress();
      const level = levelProgress?.level || 1;

      if (nameEl) nameEl.textContent = displayName.toUpperCase();
      if (mmrEl) {
        mmrEl.textContent = `${mmr} MMR`;
        mmrEl.style.display = 'block';
      }
      if (badgeEl) {
        badgeEl.textContent = level;
        badgeEl.style.display = 'inline-flex';
      }

      // Resolve equipped avatar
      let avatarUrl = null;
      const equippedArtwork = inventory.getEquippedProfileArtwork();
      if (equippedArtwork) {
        const card = CARDS_DATA.find(c => c.name === equippedArtwork);
        if (card?.images?.[0]) {
          avatarUrl = getCardThumbUrl(card.images[0], 'm');
        }
      }
      if (!avatarUrl) {
        const equippedEntry = progress.getEquippedAvatar();
        avatarUrl = resolveAvatarImageUrl(equippedEntry);
      }
      if (!avatarUrl) {
        avatarUrl = getCardThumbUrl('1_Hypermarine.png', 'm');
      }
      if (imgEl) imgEl.src = avatarUrl;

      // Frame color
      const equippedBorderId = inventory.getEquippedAvatarBorder();
      const borderStyle = equippedBorderId ? REEL_3_STYLES_MAP.get(equippedBorderId) : null;
      const frameColor = borderStyle?.hex || '#00f0ff';
      if (cardEl) {
        cardEl.style.borderColor = frameColor;
        cardEl.style.boxShadow = `0 0 20px ${frameColor}55, inset 0 0 14px ${frameColor}33`;
      }

      // Blue secured button state
      if (btnLogin) {
        btnLogin.classList.remove('status-guest');
        btnLogin.classList.add('status-linked');
      }
      if (loginText) loginText.textContent = 'COMMANDER';
    } else {
      // Guest state
      if (nameEl) nameEl.textContent = 'GUEST COMMANDER';
      if (mmrEl) mmrEl.style.display = 'none';
      if (badgeEl) badgeEl.style.display = 'none';

      let guestAvatarUrl = null;
      const guestArtwork = inventory.getEquippedProfileArtwork();
      if (guestArtwork) {
        const card = CARDS_DATA.find(c => c.name === guestArtwork);
        if (card?.images?.[0]) {
          guestAvatarUrl = getCardThumbUrl(card.images[0], 'm');
        }
      }
      if (!guestAvatarUrl) {
        guestAvatarUrl = getCardThumbUrl('1_Hypermarine.png', 'm');
      }
      if (imgEl) imgEl.src = guestAvatarUrl;
      if (cardEl) {
        cardEl.style.borderColor = 'rgba(255, 68, 85, 0.4)';
        cardEl.style.boxShadow = '0 0 16px rgba(255, 68, 85, 0.2), inset 0 0 10px rgba(255, 68, 85, 0.1)';
      }

      // Red guest button state
      if (btnLogin) {
        btnLogin.classList.remove('status-linked');
        btnLogin.classList.add('status-guest');
      }
      if (loginText) loginText.textContent = 'COMMANDER';
    }
  }

  show() {
    if (!this.container) return;
    this.container.classList.remove('hidden');
    this.container.style.display = 'flex';
    requestAnimationFrame(() => {
      this.container.classList.add('active');
    });
    this.isVisible = true;
    this.updateProfileDisplay();

    // Ensure initial history state is set to start screen so Android back closes/minimizes app
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      try {
        if (!window.history.state || window.history.state.screen !== 'start') {
          window.history.replaceState({ screen: 'start' }, '');
        }
      } catch (e) {}
    }
  }

  hide() {
    if (!this.container) return;
    this.container.classList.remove('active');
    this.isVisible = false;
    // Settle layout once #game-wrapper unfreezes from visibility:hidden (WebKit cold-start fix)
    requestAnimationFrame(() => {
      const sidebar = document.getElementById('sidebar-controls');
      if (sidebar) void sidebar.offsetHeight;
      const hand = document.getElementById('cards-hand-container');
      if (hand) void hand.offsetHeight;
    });
    setTimeout(() => {
      if (!this.isVisible) {
        this.container.classList.add('hidden');
        this.container.style.display = 'none';
      }
    }, 280);
  }
}
